import { defineComponent } from "vue";
import { refMethod, VueEmit, VueProp } from "../../core/vueHelpers";
import { Classes } from "../classes";
import { ModuleMenuScreenData } from "../menuData";
import { FocusHandler } from "./focusHandler";
import NewWindowButton from "./newWindowButton";

export interface ScreenButtonClasses
{
    unselectedClass: string;
    selectedClass: string;
    labelClass: string;
    newWindowIconClass: string;
}

export default /*#__PURE__*/ defineComponent(
{
    name: "screenButton",
    props:
    {
        moduleMenuScreenData: VueProp<ModuleMenuScreenData>(),
        screenButtonClasses: VueProp<ScreenButtonClasses>(),
        focusHandler: VueProp<FocusHandler>()
    },
    emits:
    {
        selectScreen: VueEmit<(moduleMenuScreenData: ModuleMenuScreenData, newWindow: boolean, element: HTMLElement) => void>()
    },
    data(): { forceNewWindowButtonShown: boolean; anchorEl: HTMLElement | null }
    {
        return {
            forceNewWindowButtonShown: false,
            anchorEl: null
        };
    },
    computed:
    {
        isUtility(): boolean
        {
            if (this.moduleMenuScreenData.module === null || this.moduleMenuScreenData === null || this.moduleMenuScreenData.screen === null)
            {
                return false;
            }

            const workflowScreenRegex = /^Create\/.+\/.+\/.+$/i;
            return this.moduleMenuScreenData.module === "Workflow" && this.moduleMenuScreenData.object === "WorkflowInstance" && workflowScreenRegex.test(this.moduleMenuScreenData.screen);
        }
    },
    render()
    {
        return (
            <a
                ref={refMethod<HTMLElement>((el) => {
                    this.focusHandler.setScreenButton(this.moduleMenuScreenData, el);
                    this.anchorEl = el;
                })}
                class={this.moduleMenuScreenData.selected ? this.screenButtonClasses.selectedClass : this.screenButtonClasses.unselectedClass}
                aria-label={"Go to " + this.moduleMenuScreenData.displayText}
                href="javascript:void(0)"
                aria-haspopup={this.isUtility ? "dialog" : undefined}
                onClick={(e: MouseEvent) => this._onClick(this.moduleMenuScreenData, e)}
                onKeydown={(e: KeyboardEvent) => this._onKeyDown(this.moduleMenuScreenData, e)}
                onBlur={(e: FocusEvent) => this._onBlur(e)}
                onFocusin={(e: FocusEvent) => this._onFocusIn(e)}
                onKeyup={(e: KeyboardEvent) => this._onKeyUp(e)}
                role="menuitem"
            >
                <span class={this.screenButtonClasses.labelClass}>{this.moduleMenuScreenData.displayText}</span>
                {this.moduleMenuScreenData.renderNewWindowButton && (
                    <NewWindowButton
                        moduleMenuScreenData={this.moduleMenuScreenData}
                        iconClass={this.screenButtonClasses.newWindowIconClass}
                        forceShown={this.forceNewWindowButtonShown}
                    />
                )}
            </a>
        );
    },
    methods:
    {
        _onClick(moduleMenuScreenData: ModuleMenuScreenData, e: MouseEvent): void
        {
            const target = e.target as HTMLElement;
            const newWindow = target.classList.contains(Classes.moduleScreensNewWindowButton);

            this.$emit("selectScreen", moduleMenuScreenData, newWindow, this.anchorEl!);
        },
        _onKeyDown(moduleMenuScreenData: ModuleMenuScreenData, e: KeyboardEvent): void
        {
            if (this.focusHandler.moveFocusFromScreen(moduleMenuScreenData, e))
            {
                e.preventDefault();
            }

            const eventKeys = window.skyward.keys.getKeyNamesFromEvent(e);
            let toggleOn = (eventKeys.CTRL || eventKeys.SHIFT) && !eventKeys.ARROW_DOWN && !eventKeys.ARROW_UP && !eventKeys.ARROW_LEFT && !eventKeys.ARROW_RIGHT;

            if (this.moduleMenuScreenData.renderNewWindowButton && toggleOn)
            {
                this.forceNewWindowButtonShown = true;
            }
        },
        _onKeyUp(e: KeyboardEvent): void
        {
            let toggleOff = !e.ctrlKey && !e.shiftKey;

            if (this.moduleMenuScreenData.renderNewWindowButton && toggleOff)
            {
                this.forceNewWindowButtonShown = false;
            }
        },
        _onBlur(_e: FocusEvent): void
        {
            if (this.moduleMenuScreenData.renderNewWindowButton)
            {
                this.forceNewWindowButtonShown = false;
            }
        },
        _onFocusIn(_e: FocusEvent): void
        {
            if (this.moduleMenuScreenData.renderNewWindowButton && (window.skyward.keys.globalCtrlPressed || window.skyward.keys.globalShiftPressed))
            {
                this.forceNewWindowButtonShown = true;
            }
        }
    }
});
